import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type GetClubByStoreIdOutput = OutputRouter['clubs']['getByStoreId']
export type CreateClubOutput = OutputRouter['clubs']['create']
export type UpdateClubOutput = OutputRouter['clubs']['update']
export type GetClubMembersOutput = OutputRouter['clubs']['getMembers']
export type PreviewClubOutput = OutputRouter['clubs']['preview']

// Input types
export type GetClubByStoreIdInput = InputRouter['clubs']['getByStoreId']
export type CreateClubInput = InputRouter['clubs']['create']
export type UpdateClubInput = InputRouter['clubs']['update']
export type GetClubMembersInput = InputRouter['clubs']['getMembers']
export type PreviewClubInput = InputRouter['clubs']['preview']
export type DefineMembershipProductTriggerInput = InputRouter['clubs']['defineMembershipProductTrigger']

// Query hooks
export const useClubByStoreId = (params: GetClubByStoreIdInput, enabled = true) => {
	return trpc.clubs.getByStoreId.useQuery(params, {
		enabled: enabled && !!params.storeId,
	})
}

export const useClubMembers = (params: GetClubMembersInput, enabled = true) => {
	return trpc.clubs.getMembers.useQuery(params, {
		enabled: enabled && !!params.clubId,
	})
}

// Mutation hooks
export const useCreateClub = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.clubs.create.useMutation({
		onSuccess: () => {
			utils.clubs.getByStoreId.invalidate()
			toast({
				title: 'Success',
				description: 'Club created successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to create club',
				variant: 'destructive',
			})
		},
	})
}

export const useUpdateClub = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.clubs.update.useMutation({
		onSuccess: () => {
			utils.clubs.getByStoreId.invalidate()
			toast({
				title: 'Success',
				description: 'Club updated successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to update club',
				variant: 'destructive',
			})
		},
	})
}

export const usePreviewClub = () => {
	const { toast } = useToast()

	return trpc.clubs.preview.useMutation({
		onSuccess: (data) => {
			toast({
				title: 'Success',
				description: 'Club preview URL generated',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to preview club',
				variant: 'destructive',
			})
		},
	})
}

export const useDefineMembershipProductTrigger = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.clubs.defineMembershipProductTrigger.useMutation({
		onSuccess: () => {
			utils.clubs.getByStoreId.invalidate()
			toast({
				title: 'Success',
				description: 'Club membership product trigger updated successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to update club membership product trigger',
				variant: 'destructive',
			})
		},
	})
}

